import React, { useState, useEffect } from 'react';
import { Button, message, Space, Typography, Divider, Modal, Alert } from 'antd';
import { QrcodeOutlined, CheckCircleOutlined, CloseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import QRScanner from '../components/QRScanner';
import { eventApi } from '../services/api';
import { useNavigate } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;

interface EventInfo {
  eventName: string;
  location: string;
  startTime: string;
  endTime: string;
  description?: string;
}

interface GameInfo {
  name: string;
  description?: string;
}

interface JoinResult {
  success: boolean;
  message: string;
  gameSessionId?: string;
  eventInfo?: EventInfo;
  gameInfo?: GameInfo;
  attemptInfo?: {
    attemptNumber: number;
    remainingAttempts: number;
  };
}

const EventJoinPage: React.FC = () => {
  const [scanning, setScanning] = useState(false);
  const [joinResult, setJoinResult] = useState<JoinResult | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleScanSuccess = async (qrToken: string) => {
    setLoading(true);
    try {
      const result = await eventApi.verifyAndJoinEvent(qrToken);
      
      // Store gameSessionId in session storage for the racing game
      if (result.gameSessionId) {
        sessionStorage.setItem('gameSessionId', result.gameSessionId);
      }
      
      setJoinResult({
        success: true,
        message: 'Tham gia sự kiện thành công!',
        gameSessionId: result.gameSessionId,
        eventInfo: result.eventInfo,
        attemptInfo: result.attemptInfo
      });
      setScanning(false);
      message.success('Đã tham gia sự kiện thành công!');
    } catch (error: any) {
      // Handle authentication errors specifically
      if (error.message === 'Authentication required. Please login first.' || 
          error.response?.status === 401) {
        const errorMessage = 'Bạn cần đăng nhập để tham gia sự kiện. Vui lòng đăng nhập và thử lại.';
        setJoinResult({
          success: false,
          message: errorMessage
        });
        message.error(errorMessage);
      } else {
        const errorMessage = error.response?.data?.message || 'Không thể tham gia sự kiện';
        setJoinResult({
          success: false,
          message: errorMessage
        });
        message.error(errorMessage);
      }
      setScanning(false);
    } finally {
      setLoading(false);
    }
  };

  const handleScanError = (error: string) => {
    message.error(`Lỗi quét QR: ${error}`);
    setScanning(false);
  };

  const startScanning = () => {
    setScanning(true);
    setJoinResult(null);
  };

  const stopScanning = () => {
    setScanning(false);
  };

  const resetAndScanAgain = () => {
    setJoinResult(null);
    setScanning(true);
  };

  const playGame = () => {
    // Navigate to racing game with event session
    navigate('/kiloba-racing');
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
      <div style={{ background: '#fff', padding: '24px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <QrcodeOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
          <Title level={2}>Tham gia sự kiện</Title>
          <Paragraph type="secondary">
            Quét mã QR tại địa điểm sự kiện để tham gia và nhận phần thưởng
          </Paragraph>
        </div>

        {!scanning && !joinResult && (
          <div style={{ textAlign: 'center' }}>
            <Button
              type="primary"
              size="large"
              icon={<QrcodeOutlined />}
              onClick={startScanning}
              style={{ minWidth: '200px' }}
            >
              Quét mã QR
            </Button>
            
            <Divider />
            
            <Alert
              message="Hướng dẫn sử dụng"
              description={
                <div>
                  <p>• Tìm màn hình hiển thị mã QR tại địa điểm sự kiện</p>
                  <p>• Nhấn nút "Quét mã QR" và di chuyển camera về phía mã QR</p>
                  <p>• Đảm bảo mã QR nằm trong khung quét</p>
                  <p>• Chờ hệ thống xác nhận tham gia thành công</p>
                </div>
              }
              type="info"
              showIcon
              style={{ textAlign: 'left' }}
            />
          </div>
        )}

        {scanning && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <Title level={4}>Đang quét mã QR...</Title>
              <Text type="secondary">Hướng camera vào mã QR tại địa điểm sự kiện</Text>
            </div>
            
            <QRScanner
              onScanSuccess={handleScanSuccess}
              onScanError={handleScanError}
              loading={loading}
            />
            
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <Button onClick={stopScanning} disabled={loading}>
                Hủy quét
              </Button>
            </div>
          </div>
        )}

        {joinResult && (
          <div style={{ textAlign: 'center' }}>
            {joinResult.success ? (
              <div>
                <CheckCircleOutlined style={{ fontSize: '64px', color: '#52c41a', marginBottom: '16px' }} />
                <Title level={3} style={{ color: '#52c41a' }}>
                  Tham gia thành công!
                </Title>
                
                {joinResult.attemptInfo && (
                  <div style={{ margin: '16px 0', padding: '12px', background: '#e6f7ff', borderRadius: '6px', border: '1px solid #91d5ff' }}>
                    <Text strong style={{ color: '#1890ff' }}>
                      Lần chơi: {joinResult.attemptInfo.attemptNumber}/3
                    </Text>
                    {joinResult.attemptInfo.remainingAttempts > 0 && (
                      <Text style={{ display: 'block', fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        Còn {joinResult.attemptInfo.remainingAttempts} lần chơi
                      </Text>
                    )}
                  </div>
                )}
                
                                {joinResult.eventInfo && (
                  <div style={{ marginTop: '20px', textAlign: 'left', background: '#f9f9f9', padding: '16px', borderRadius: '6px' }}>
                    <Title level={4}>Thông tin sự kiện</Title>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text strong>Tên sự kiện: </Text>
                        <Text>{joinResult.eventInfo.eventName}</Text>
                      </div>
                      <div>
                        <Text strong>Địa điểm: </Text>
                        <Text>{joinResult.eventInfo.location}</Text>
                      </div>
                      <div>
                        <Text strong>Thời gian: </Text>
                        <Text>{joinResult.eventInfo.startTime} - {joinResult.eventInfo.endTime}</Text>
                      </div>
                      {joinResult.gameInfo && (
                        <div>
                          <Text strong>Game: </Text>
                          <Text>{joinResult.gameInfo.name}</Text>
                        </div>
                      )}
                    </Space>
                  </div>
                )}
                
                <Space style={{ marginTop: '20px' }}>
                  <Button 
                    type="primary" 
                    icon={<PlayCircleOutlined />}
                    size="large"
                    onClick={playGame}
                  >
                    Chơi game
                  </Button>
                  <Button onClick={resetAndScanAgain}>
                    Quét sự kiện khác
                  </Button>
                  <Button onClick={() => window.history.back()}>
                    Quay lại
                  </Button>
                </Space>
              </div>
            ) : (
              <div>
                <CloseCircleOutlined style={{ fontSize: '64px', color: '#ff4d4f', marginBottom: '16px' }} />
                <Title level={3} style={{ color: '#ff4d4f' }}>
                  Không thể tham gia
                </Title>
                <Paragraph style={{ marginBottom: '20px' }}>
                  {joinResult.message}
                </Paragraph>
                
                <Space>
                  <Button type="primary" onClick={resetAndScanAgain}>
                    Thử lại
                  </Button>
                  <Button onClick={() => window.history.back()}>
                    Quay lại
                  </Button>
                </Space>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventJoinPage;
